import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { Sun, CheckCircle2, FileDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Palette barre mensili (dal design): dal bruno spento all'ambra piena
const BAR_COLORS = ["#3F3A2A", "#57492B", "#6E5A2C", "#8A6E2D", "#B08A2E", "#E0A82E"];
const MONTH_LABELS = ["G", "F", "M", "A", "M", "G", "L", "A", "S", "O", "N", "D"];
// Curva di fallback se la simulazione non ha i dati mensili
const FALLBACK_CURVE = [0.35, 0.45, 0.6, 0.75, 0.88, 1, 0.98, 0.86, 0.68, 0.5, 0.38, 0.3];

// Formattazione italiana deterministica (non dipende dal locale del browser)
const sep = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const eur2 = (n) => {
  const [i, d] = (Math.round((n || 0) * 100) / 100).toFixed(2).split(".");
  return `€ ${sep(i)},${d}`;
};
const eur0 = (n) => `€ ${sep(String(Math.round(n || 0)))}`;
const itDec = (n) => String(n ?? "—").replace(".", ",");

function MonthlyBars({ values }) {
  const max = Math.max(...values, 1);
  return (
    <>
      <div className="flex items-end gap-[5px] h-16 mt-auto pt-3.5">
        {values.map((v, i) => {
          const p = v / max;
          const color = BAR_COLORS[Math.round(p * (BAR_COLORS.length - 1))];
          return <div key={i} className="flex-1 rounded-t-[3px]" style={{ height: `${Math.max(8, p * 100)}%`, background: color }} />;
        })}
      </div>
      <div className="flex gap-[5px] mt-1.5 font-mono text-[7px] text-[#52525B]">
        {MONTH_LABELS.map((m, i) => <span key={i} className="flex-1 text-center">{m}</span>)}
      </div>
    </>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [signName, setSignName] = useState("");
  const padRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/proposals/${token}`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        const c = d?.customer;
        const nm = c?.business_name || `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
        if (nm) setSignName(nm);
      })
      .catch(() => setData(null));
  }, [token]);

  const padPos = (e) => {
    const c = padRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    return { x: (clientX - r.left) * (c.width / r.width), y: (clientY - r.top) * (c.height / r.height) };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = padRef.current?.getContext("2d");
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = padPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = padRef.current?.getContext("2d");
    if (!ctx) return;
    hasInk.current = true;
    const { x, y } = padPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#18181b";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const endDraw = (e) => {
    e?.preventDefault?.();
    drawing.current = false;
  };

  const clearPad = () => {
    const c = padRef.current;
    const ctx = c?.getContext("2d");
    if (ctx && c) ctx.clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
  };

  const accept = async () => {
    if (!signName.trim()) {
      toast.error("Inserisci il tuo nome per firmare");
      return;
    }
    setAccepting(true);
    try {
      if (!hasInk.current) {
        toast.error("Disegna la firma nel riquadro");
        setAccepting(false);
        return;
      }
      const canvas = padRef.current;
      const signature_image_base64 = canvas ? canvas.toDataURL("image/png") : null;
      const res = await fetch(`${API_BASE}/public/proposals/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_name: signName.trim(), signature_image_base64 }),
      });
      if (!res.ok) {
        toast.error("Impossibile completare l'accettazione. Riprova.");
        return;
      }
      setData((d) => ({ ...d, quote: { ...d.quote, status: "accepted", signature_name: signName.trim() } }));
      toast.success("Preventivo accettato! Verrai contattato a breve.");
    } catch {
      toast.error("Impossibile completare l'accettazione. Riprova.");
    } finally {
      setAccepting(false);
    }
  };

  if (!data) return <div className="min-h-screen bg-[#f4f3f0] flex items-center justify-center text-zinc-500 font-mono">Caricamento…</div>;
  const { quote, customer, company, simulation, economics } = data;
  const brand = company?.name || "SolarFlow";
  const cname = customer?.business_name || `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() || "Cliente";
  const r = simulation?.results;
  const accepted = quote.status === "accepted";
  const dateStr = quote.created_at ? quote.created_at.slice(0, 10).split("-").reverse().join("/") : "";

  const monthly = (r?.monthly_production_kwh?.length === 12 ? r.monthly_production_kwh : FALLBACK_CURVE);
  // Rientro dal piano economico (include la detrazione fiscale) se disponibile, altrimenti quello semplice
  const payback = (economics?.payback_year != null ? Math.round(economics.payback_year * 10) / 10 : r?.payback_years);
  const paybackDetr = !!economics?.has_detrazione;
  const paybackPct = payback ? Math.min(100, (payback / 25) * 100) : 0;

  // Detrazione: incentivi abilitati di tipo "detrazione", altrimenti % legacy del privato
  const detrIncs = (customer?.incentives || []).filter((i) => i.enabled !== false && (i.kind ?? "detrazione") === "detrazione");
  const detrPct = detrIncs.length
    ? detrIncs.reduce((s, i) => s + (parseFloat(i.pct) || 0), 0)
    : (customer?.type === "private" ? parseFloat(customer?.fiscal_deduction_pct) || 0 : 0);
  const detrazione = (quote.total || 0) * detrPct / 100;
  const nettoDetrazione = (quote.total || 0) + (quote.gazebo_cost || 0) - detrazione;

  return (
    <div className="min-h-screen bg-[#f4f3f0] text-[#18181B] py-6 sm:py-10 px-3 sm:px-4">
      <div className="max-w-[820px] mx-auto bg-white rounded-2xl shadow-[0_12px_44px_rgba(0,0,0,0.10)] px-5 py-7 sm:px-12 sm:py-12">

        {/* header */}
        <div className="flex justify-between items-center pb-3.5 border-b border-[#E4E4E7] gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="w-[30px] h-[30px] rounded-[10px] bg-[#E0A82E] flex items-center justify-center text-[#18181B]"><Sun size={17} strokeWidth={2.5} /></span>
            <span className="text-[19px] font-extrabold tracking-[-0.03em]">{brand}</span>
          </div>
          <div className="text-right font-mono text-[10px] sm:text-[11px] leading-relaxed text-[#71717A]">Preventivo N. {quote.quote_number}{dateStr ? ` · ${dateStr}` : ""}</div>
        </div>

        {/* titolo */}
        <div className="mt-4">
          <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-[#E0A82E] font-semibold">Riepilogo e accettazione</div>
          <h1 className="mt-1 text-[24px] sm:text-[28px] font-extrabold tracking-[-0.02em]">Quanto produci, quanto risparmi</h1>
        </div>

        {/* numeri chiave */}
        {r && (
          <div className="mt-4 bg-[#18181B] rounded-[18px] p-5 sm:p-6 text-white grid sm:grid-cols-[1.15fr_1fr] gap-6 sm:gap-7">
            <div className="flex flex-col">
              <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#71717A]">Produzione stimata / anno</div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-mono text-[34px] sm:text-[40px] font-semibold tracking-[-0.02em]">{Math.round(r.annual_production_kwh).toLocaleString("it-IT")}</span>
                <span className="text-[13px] text-[#A1A1AA]">kWh</span>
              </div>
              <MonthlyBars values={monthly} />
            </div>
            <div className="flex flex-col justify-between sm:border-l border-[#27272A] sm:pl-7">
              <div className="pt-1 pb-3 border-b border-[#27272A]">
                <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#71717A]">Risparmio / anno</div>
                <div className="font-mono text-[26px] font-semibold text-[#E8B84B] mt-1">{eur0(r.annual_saving_eur)}</div>
              </div>
              <div className="py-3 border-b border-[#27272A]">
                <div className="flex justify-between items-baseline">
                  <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#71717A]">Payback{paybackDetr ? " · con detrazione" : ""}</div>
                  <div className="font-mono text-[19px] font-semibold">{itDec(payback || "—")} <span className="text-[11px] text-[#A1A1AA]">anni</span></div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-[#27272A] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#E8B84B] to-[#E0A82E]" style={{ width: `${paybackPct}%` }} />
                </div>
                <div className="flex justify-between mt-1 font-mono text-[7.5px] text-[#52525B]">
                  <span>0</span><span>{payback ? `rientro · anno ${Math.round(payback)}` : ""}</span><span>25</span>
                </div>
              </div>
              <div className="flex justify-between items-baseline pt-3">
                <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#71717A]">CO₂ evitata / anno</div>
                <div className="font-mono text-[19px] font-semibold">{itDec(r.co2_avoided_tonnes_year)} <span className="text-[11px] text-[#A1A1AA]">t</span></div>
              </div>
            </div>
          </div>
        )}

        {/* dettaglio + note */}
        <div className="grid sm:grid-cols-[1.15fr_0.85fr] gap-5 mt-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.02em] mb-2">Componenti e servizi inclusi</div>
            <div className="flex flex-col gap-[7px] text-[10.5px] text-[#3f3f46]">
              {quote.items.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-[#E0A82E] font-bold">›</span>
                  <span>{it.description}{parseFloat(it.quantity) > 1 ? ` · ${it.quantity} pz` : ""}</span>
                </div>
              ))}
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.02em] mt-4 mb-2">Il tuo investimento</div>
            <div className="text-[10.5px]">
              <div className="flex justify-between gap-2.5 py-1.5 border-b border-[#F1F1F0]"><span className="text-[#71717A]">Subtotale</span><span className="font-mono font-medium">{eur2(quote.subtotal)}</span></div>
              {quote.discount > 0 && (
                <div className="flex justify-between gap-2.5 py-1.5 border-b border-[#F1F1F0]"><span className="text-[#71717A]">Sconto</span><span className="font-mono font-medium text-[#1B7A4A]">− {eur2(quote.discount)}</span></div>
              )}
              <div className="flex justify-between gap-2.5 py-1.5 border-b border-[#F1F1F0]"><span className="text-[#71717A]">IVA ({Math.round((quote.tax_rate || 0) * 100)}%)</span><span className="font-mono font-medium">{eur2(quote.tax_amount)}</span></div>
              <div className="flex justify-between gap-2.5 py-2 px-1.5 bg-[#18181B] rounded-md my-[3px] -mx-1.5"><span className="font-bold text-white">Totale chiavi in mano</span><span className="font-mono font-semibold text-[#E8B84B]">{eur2(quote.total)}</span></div>
              {quote.gazebo_cost > 0 && (
                <>
                  <div className="flex justify-between gap-2.5 py-1.5 border-b border-[#F1F1F0]"><span className="text-[#71717A]">Supplemento gazebo</span><span className="font-mono font-medium">{eur2(quote.gazebo_cost)}</span></div>
                  <div className="flex justify-between gap-2.5 py-2 px-1.5 bg-[#FBF6E9] border border-[#E7D9A8] rounded-md my-[3px] -mx-1.5"><span className="font-bold text-[#A16207]">Totale con gazebo</span><span className="font-mono font-semibold text-[#A16207]">{eur2((quote.total || 0) + quote.gazebo_cost)}</span></div>
                </>
              )}
              {detrazione > 0 && (
                <>
                  <div className="flex justify-between gap-2.5 py-1.5 border-b border-[#F1F1F0]"><span className="text-[#71717A]">Detrazione fiscale {itDec(detrPct)}%</span><span className="font-mono font-medium text-[#1B7A4A]">− {eur2(detrazione)}</span></div>
                  <div className="flex justify-between gap-2.5 py-2 px-1.5 bg-[#E6F2EA] rounded-md mt-[3px] -mx-1.5"><span className="font-bold text-[#1B7A4A]">A netto della detrazione</span><span className="font-mono font-semibold text-[#1B7A4A]">{eur2(nettoDetrazione)}</span></div>
                </>
              )}
            </div>
          </div>
          <div className="border border-[#E4E4E7] rounded-[14px] py-3.5 px-4 bg-[#f4f3f0] self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#A16207] mb-2">Note</div>
            <div className="flex flex-col gap-[7px] text-[10px] text-[#52525B] leading-normal">
              <div className="flex gap-[7px]"><span className="text-[#E0A82E] font-bold">›</span>Preventivo valido 30 giorni dalla data di emissione.</div>
              <div className="flex gap-[7px]"><span className="text-[#E0A82E] font-bold">›</span>Importi comprensivi di IVA come da dettaglio costi.</div>
              <div className="flex gap-[7px]"><span className="text-[#E0A82E] font-bold">›</span>Stime di produzione preliminari su base PVGIS, non garantite.</div>
            </div>
          </div>
        </div>

        {/* firma / accettazione */}
        <div className="mt-4 border border-[#E4E4E7] rounded-2xl py-4 px-4 sm:px-[18px]">
          <div className="text-xs font-bold uppercase tracking-[0.02em] mb-3">Accettazione e firma</div>
          {accepted ? (
            <div className="bg-[#E6F2EA] border border-[#A7E3C2] rounded-xl px-5 py-4 flex items-center gap-3 text-[#1B7A4A]">
              <CheckCircle2 size={20} />
              <div>
                <div className="font-semibold text-sm">Proposta accettata · grazie!</div>
                {quote.signature_name && <div className="text-xs mt-0.5">Firmato da {quote.signature_name}</div>}
              </div>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-[1fr_1.3fr] gap-6 items-end">
                <div>
                  <label className="font-mono text-[8.5px] tracking-[0.1em] uppercase text-[#71717A] block mb-1.5" htmlFor="sig-name">Il tuo nome</label>
                  <input id="sig-name" type="text" value={signName} onChange={(e) => setSignName(e.target.value)} placeholder={cname}
                    className="w-full text-[15px] font-bold pb-2 border-b-[1.5px] border-[#18181B] bg-transparent focus:outline-none placeholder:text-zinc-300" data-testid="signature-name" />
                  <div className="flex justify-between mt-4 font-mono text-[9.5px] text-[#52525B] gap-2"><span>Luogo {company?.address || "______________"}</span><span className="shrink-0">Data {dateStr}</span></div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="font-mono text-[8.5px] tracking-[0.1em] uppercase text-[#71717A]">Firma qui sotto</div>
                    <button type="button" onClick={clearPad} className="font-mono text-[9px] text-[#71717A] border border-[#E4E4E7] rounded-[7px] px-2 py-[3px] hover:text-zinc-900 transition-colors" data-testid="signature-clear">Cancella firma</button>
                  </div>
                  <canvas ref={padRef} width={760} height={220}
                    onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                    onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                    className="w-full h-auto block border-[1.5px] border-dashed border-[#D4D4D8] rounded-xl bg-[#FDFDFC] touch-none cursor-crosshair" data-testid="signature-pad" />
                </div>
              </div>
              <div className="mt-3 text-[8.5px] text-[#A1A1AA] leading-normal">Firmando dichiari di accettare la proposta commerciale e le condizioni allegate. Le stime di produzione sono preliminari, basate su modelli statistici (PVGIS), e non costituiscono garanzia.</div>
              <button type="button" onClick={accept} disabled={accepting}
                className="mt-4 w-full bg-[#18181B] text-white rounded-full px-6 py-3.5 font-semibold hover:bg-zinc-800 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="public-accept-btn">
                {accepting ? "Conferma…" : <>Firma e accetta <ArrowRight size={16} /></>}
              </button>
            </>
          )}
        </div>

        {/* footer */}
        <div className="flex justify-between mt-6 pt-2 border-t border-[#E4E4E7] font-mono text-[9px] text-[#A1A1AA] gap-2 flex-wrap">
          <span>{brand} · Riepilogo e accettazione</span>
          {company?.email && <span>{company.email}</span>}
        </div>
      </div>

      {/* azioni sotto il foglio */}
      <div className="max-w-[820px] mx-auto mt-4 flex flex-col sm:flex-row gap-3">
        <a href={`${API_BASE}/public/proposals/${token}/pdf`} target="_blank" rel="noreferrer"
          className="flex-1 px-6 py-3.5 bg-white border border-zinc-200 rounded-full flex items-center justify-center gap-2 font-medium text-sm hover:border-zinc-300 transition-colors" data-testid="public-pdf-btn">
          <FileDown size={15} /> Scarica PDF
        </a>
        <a href={`https://wa.me/?text=${encodeURIComponent(`Ciao! Ecco il mio preventivo ${brand}: ${window.location.href}`)}`} target="_blank" rel="noreferrer"
          className="flex-1 px-6 py-3.5 bg-emerald-500 text-white rounded-full flex items-center justify-center gap-2 font-medium text-sm hover:bg-emerald-600 transition-colors" data-testid="public-whatsapp-btn">
          WhatsApp
        </a>
      </div>
    </div>
  );
}
